/** Screen Wake Lock: best effort, never required for the timer to work. */
export type WakeLockStatus = 'active' | 'unsupported' | 'failed' | 'released';

export const WAKE_LOCK_LABELS: Record<WakeLockStatus, string> = {
  active: 'Aktiv',
  unsupported: 'Browser unterstützt dies nicht',
  failed: 'Nicht möglich (Displaysperre am iPad deaktivieren)',
  released: 'Nicht aktiv',
};

interface WakeLockSentinelLike {
  released: boolean;
  release(): Promise<void>;
  addEventListener(type: 'release', listener: () => void): void;
}

export function startWakeLock(onStatus: (status: WakeLockStatus) => void): () => void {
  const nav = navigator as Navigator & {
    wakeLock?: { request(type: 'screen'): Promise<WakeLockSentinelLike> };
  };
  if (!nav.wakeLock) {
    onStatus('unsupported');
    return () => {};
  }
  let sentinel: WakeLockSentinelLike | null = null;
  let stopped = false;

  const request = async () => {
    if (stopped || document.visibilityState !== 'visible') return;
    if (sentinel && !sentinel.released) return;
    try {
      sentinel = await nav.wakeLock!.request('screen');
      onStatus('active');
      sentinel.addEventListener('release', () => {
        if (!stopped) onStatus('released');
      });
    } catch (err) {
      console.warn('[wake-lock] request failed', err);
      onStatus('failed');
    }
  };

  const onVisible = () => {
    if (document.visibilityState === 'visible') void request();
  };
  document.addEventListener('visibilitychange', onVisible);
  // Some browsers grant wake locks only after a user gesture: retry on first tap.
  const onInteract = () => void request();
  document.addEventListener('click', onInteract, { passive: true });
  void request();

  return () => {
    stopped = true;
    document.removeEventListener('visibilitychange', onVisible);
    document.removeEventListener('click', onInteract);
    void sentinel?.release().catch(() => {});
  };
}
